// C:\Users\avnis\CareCo-OrdinatorDashboard\client\src\CareCoordinator\data\enhancemonitoring.js
//
// Data layer for the "Enhanced Monitoring" live heart-rate feature.
// Talks to GET /api/real-time/heart-rate/stream, which is a
// Server-Sent-Events (SSE) endpoint protected by `get_current_user`.
//
// Native `EventSource` can't attach an Authorization header, so if this
// app's auth relies on a bearer token (not just a cookie), EventSource
// won't authenticate. To stay safe for both setups, we drive the stream
// manually over `fetch` (which lets us send an Authorization header AND
// cookies) and parse the SSE wire format ourselves.
//
// If your auth is 100% cookie/session based, you can simplify this file
// down to a plain `new EventSource(url, { withCredentials: true })` —
// but the fetch-based version below works either way, so it's the safer
// default.

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STREAM_PATH = "/api/real-time/heart-rate/stream";

// Matches the token lookup used by fetchHealthTrends() etc. in this
// codebase (../data/*), so the stream authenticates the same way every
// other API call here does.
function getAuthToken() {
  try {
    return localStorage.getItem("token");
  } catch {
    return null;
  }
}

/**
 * Normalises whatever shape the backend sends into a plain finite number
 * (bpm), or null if it can't be parsed. Handles:
 *  - a bare number                -> 78
 *  - a numeric string             -> "78"
 *  - an object carrying the value -> { heart_rate: 78, recorded_at: "..." }
 */
export function extractHeartRateValue(payload) {
  if (payload == null) return null;

  if (typeof payload === "number") {
    return Number.isFinite(payload) ? payload : null;
  }

  if (typeof payload === "string") {
    const n = Number(payload);
    return Number.isFinite(n) ? n : null;
  }

  if (typeof payload === "object") {
    // Backend wraps every event as:
    //   { status: "success", message: "...", data: { hr, ts, session_id } }
    // The "connected" event has the same envelope with an empty `data: {}`,
    // which correctly falls through to null below and gets ignored.
    const inner =
      payload.data && typeof payload.data === "object" ? payload.data : payload;

    const candidate =
      inner.hr ??
      inner.heart_rate ??
      inner.heartRate ??
      inner.bpm ??
      inner.value ??
      null;
    const n = Number(candidate);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

/**
 * Pulls a timestamp out of the payload if the backend sends one. Handles
 * both the nested `data.ts` (epoch milliseconds, as sent by the
 * heart-rate stream) and a few common top-level date-string shapes.
 * Returns null if there isn't one — the caller can just fall back to "now".
 */
export function extractHeartRateTimestamp(payload) {
  if (!payload || typeof payload !== "object") return null;

  const inner =
    payload.data && typeof payload.data === "object" ? payload.data : payload;

  if (typeof inner.ts === "number") return inner.ts; // epoch ms

  return (
    inner.recorded_at ||
    inner.updated_at ||
    inner.timestamp ||
    inner.time ||
    null
  );
}

/**
 * Opens a live connection to the heart-rate SSE stream and invokes
 * `onData` with each parsed reading as it arrives. Auto-reconnects
 * (with a short backoff) if the connection drops, until `.close()`
 * is called.
 *
 * Usage:
 *   const stream = openHeartRateStream({
 *     ownerId: patient.owner_id,
 *     onOpen:  () => setStatus("live"),
 *     onData:  (payload) => { ... },
 *     onError: (err) => setStatus("error"),
 *     onClose: () => setStatus("reconnecting"),
 *   });
 *   // later
 *   stream.close();
 */
export function openHeartRateStream({
  ownerId,
  onData,
  onOpen,
  onError,
  onClose,
  reconnectDelayMs = 3000,
} = {}) {
  const controller = new AbortController();
  let closed = false;
  let retryTimer = null;

  // The backend route is `GET /real-time/heart-rate/stream?patient_owner_id=...`.
  // `patient_owner_id` is optional there: a care_coordinator viewing a
  // patient's own stream must pass it (the endpoint 403s a non-coordinator
  // who tries), while omitting it just streams the caller's own heart rate.
  const url = `${BASE_URL}${STREAM_PATH}${
    ownerId ? `?${new URLSearchParams({ patient_owner_id: ownerId })}` : ""
  }`;

  async function connect() {
    if (closed) return;

    try {
      const token = getAuthToken();
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Heart-rate stream failed with status ${res.status}`);
      }

      onOpen && onOpen();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line ("\n\n").
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? ""; // keep the last (possibly incomplete) chunk

        for (const rawEvent of events) {
          const dataLines = rawEvent
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim());

          if (dataLines.length === 0) continue;

          const payloadText = dataLines.join("\n");
          let parsed = payloadText;
          try {
            parsed = JSON.parse(payloadText);
          } catch {
            // Not JSON — pass the raw string through as-is.
          }

          onData && onData(parsed);
        }
      }

      if (!closed) {
        onClose && onClose();
        scheduleReconnect();
      }
    } catch (err) {
      if (closed || err.name === "AbortError") return;
      onError && onError(err);
      scheduleReconnect();
    }
  }

  function scheduleReconnect() {
    if (closed) return;
    retryTimer = setTimeout(connect, reconnectDelayMs);
  }

  connect();

  return {
    close() {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller.abort();
    },
  };
}